import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, mangaList } from '@/mocks/siteData';

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentCategory = searchParams.get('c') || 'all';
  const [filteredManga, setFilteredManga] = useState(mangaList);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentCategory]);

  useEffect(() => {
    if (currentCategory === 'all') {
      setFilteredManga(mangaList);
    } else {
      setFilteredManga(mangaList.filter((m) => m.category === currentCategory));
    }
  }, [currentCategory]);

  const handleCategoryChange = (catId: string) => {
    if (catId === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ c: catId });
    }
  };

  const currentCatName = categories.find((c) => c.id === currentCategory)?.name || '全部';
  const siteUrl = `https://jmcomic.com/category`;

  return (
    <>
      <SEOHead
        title={`${currentCatName}漫画 - 禁漫天堂在线分类浏览`}
        description={`禁漫天堂在线${currentCatName}漫画分类，海量正版${currentCatName}题材成人漫画在线阅读，涵盖多种风格与类型，畅享优质漫画资源。`}
        keywords={`${currentCatName}漫画,禁漫天堂在线,成人漫画分类,漫画在线阅读,禁漫天堂`}
        canonicalUrl={siteUrl}
      />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jmcomic.com' },
          { '@type': 'ListItem', position: 2, name: '分类浏览', item: siteUrl },
        ],
      }} />
      <SchemaOrg type="WebPage" data={{
        name: `${currentCatName}漫画 - 禁漫天堂在线分类浏览`,
        url: siteUrl,
        description: `禁漫天堂在线${currentCatName}漫画分类，海量正版成人漫画在线阅读。`,
        isPartOf: { '@type': 'WebSite', name: '禁漫天堂在线', url: 'https://jmcomic.com' },
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              { label: '首页', path: '/' },
              { label: '分类浏览' },
            ]} />

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-2">
                漫画分类浏览
              </h1>
              <p className="text-sm text-foreground-500">
                按分类浏览海量漫画资源，发现你喜爱的题材与风格
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    currentCategory === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-secondary-100'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            {filteredManga.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-5xl text-foreground-300 block mb-4"></i>
                <p className="text-foreground-500">该分类下暂无漫画，请浏览其他分类</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6 mb-12">
                {filteredManga.map((manga) => (
                  <Link
                    key={manga.id}
                    to={`/article/${manga.id}`}
                    className="group block"
                  >
                    <div className="aspect-[2/3] overflow-hidden rounded-lg bg-background-200 mb-3">
                      <img
                        src={manga.cover}
                        alt={manga.title}
                        title={`${manga.title} - 禁漫天堂在线`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {manga.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-1 line-clamp-1">{manga.author}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-xs text-accent-500 flex items-center gap-0.5 whitespace-nowrap">
                        <i className="ri-star-fill text-[10px]"></i>
                        {manga.rating}
                      </span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{manga.chapters}话</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded whitespace-nowrap ${
                        manga.status === '连载中'
                          ? 'bg-accent-100 text-accent-700'
                          : 'bg-secondary-200 text-secondary-700'
                      }`}>
                        {manga.status}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}